import { useEffect, useState, useCallback, useRef } from 'react';
import { Button, Steps, Typography } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { wizardIcons } from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/constants/wizardIcons';

import { useWizard } from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/context/useWizard';
import useStepGroups from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/hooks/useStepGroups';
import useStepStatus from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/hooks/useStepStatus';

const { Step } = Steps;

const StepTitle = ({ step, isNavigable, isExpanded, onToggle, onNavigate }) => {
  const label = (
    <Typography.Text ellipsis={{ tooltip: step.title }}>{step.title}</Typography.Text>
  );

  if (step.isGroup) {
    return (
      <Button
        type='text'
        className='wizard-steps__title'
        aria-expanded={isExpanded}
        onClick={onToggle}
      >
        {label}
        <DownOutlined className='wizard-steps__caret' />
      </Button>
    );
  }

  if (!isNavigable) {
    return <span className='wizard-steps__title'>{label}</span>;
  }

  return (
    <Button
      type='text'
      className='wizard-steps__title'
      aria-current={step.status === 'process' ? 'step' : undefined}
      onClick={onNavigate}
    >
      {label}
    </Button>
  );
};

const WizardSteps = () => {
  const { state, actions } = useWizard();
  const { currentStep, isBypassed } = state;
  const stepsContainerRef = useRef(null);

  const [expandedGroups, setExpandedGroups] = useState({
    productInformation: true,
    packagingStorage: true,
    recommendation: true,
  });

  const stepGroups = useStepGroups({
    isBypassed,
    isTerminal: currentStep === 'contactSales',
    productCount: state?.answers?.productCount,
  });

  const {
    canNavigateToStep,
    currentGroupInfo,
    getGroupStatus,
    getSubstepStatus,
  } = useStepStatus(currentStep, stepGroups);

  // Auto-expand current group
  useEffect(() => {
    if (currentGroupInfo.group?.type === 'group') {
      setExpandedGroups((prev) => ({
        ...prev,
        [currentGroupInfo.group.key]: true,
      }));
    }
  }, [currentGroupInfo]);

  useEffect(() => {
    const container = stepsContainerRef.current;
    if (!container) return;

    const timer = setTimeout(() => {
      const activeItem =
        container.querySelector('.substep.ant-steps-item-process') ||
        container.querySelector('.ant-steps-item-process');
      if (!activeItem) return;

      let scrollParent = activeItem.parentElement;
      while (
        scrollParent &&
        !scrollParent.classList.contains('wizard-sidebar')
      ) {
        scrollParent = scrollParent.parentElement;
      }
      if (!scrollParent) return;

      const itemOffsetTop =
        activeItem.getBoundingClientRect().top -
        scrollParent.getBoundingClientRect().top +
        scrollParent.scrollTop;
      const itemHeight = activeItem.offsetHeight;
      const parentHeight = scrollParent.offsetHeight;

      scrollParent.scrollTo({
        top: itemOffsetTop - parentHeight / 2 + itemHeight / 2,
        behavior: 'smooth',
      });
    }, 100);

    return () => clearTimeout(timer);
  }, [currentStep]);

  const toggleGroup = useCallback((groupKey) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }));
  }, []);

  const handleStepClick = useCallback(
    (stepKey) => {
      if (stepKey && canNavigateToStep(stepKey)) {
        if (stepKey === 'intro') {
          actions.setBypassed(false);
        }
        actions.goToStep(stepKey);
      }
    },
    [actions, canNavigateToStep]
  );

  const visibleSteps = stepGroups.flatMap((group) => [
    {
      ...group,
      status: getGroupStatus(group),
      isGroup: group.type === 'group',
      isChild: false,
    },
    ...(group.type === 'group' && expandedGroups[group.key]
      ? group.substeps.map((substep, index) => ({
          ...substep,
          stepKey: substep.key,
          status: getSubstepStatus(group, index),
          isGroup: false,
          isChild: true,
        }))
      : []),
  ]);

  const getStepClassName = (step) => {
    if (step.isChild) return 'substep';
    if (!step.isGroup) return undefined;
    return expandedGroups[step.key]
      ? 'wizard-steps__group'
      : 'wizard-steps__group wizard-steps__group--collapsed';
  };

  return (
    <div className='wizard-steps' ref={stepsContainerRef}>
      <Steps direction='vertical' size='small'>
        {visibleSteps.map((step) => {
          const Icon = wizardIcons[step.key];
          return (
            <Step
              key={step.key}
              status={step.status}
              icon={Icon && <Icon />}
              className={getStepClassName(step)}
              title={
                <StepTitle
                  step={step}
                  isNavigable={canNavigateToStep(step.stepKey)}
                  isExpanded={!!expandedGroups[step.key]}
                  onToggle={() => toggleGroup(step.key)}
                  onNavigate={() => handleStepClick(step.stepKey)}
                />
              }
            />
          );
        })}
      </Steps>
    </div>
  );
};

export default WizardSteps;
